/**
 * Career Dashboard v2 — fully deterministic 8-family / 30-field career
 * report. Replaces the v1 LLM-narrative-centric dashboard.
 *
 * Layers:
 *   1. Plain-language takeaways (5 bullets)
 *   2. Career direction (lead family + secondary + why_not)
 *   3. Recommended families (top 3 with sub-fields)
 *   4. Other families (collapsible — remaining 5)
 *   5. Timing (current dasha + next 5-year)
 *   6. RIASEC × chart alignment (when persona has RIASEC)
 *   7. BNN section (Saturn-axis)
 *   8. Inner Practice (iṣṭa-deva + bhāvanā contemplations + bhāvanā yoga
 *      affirmations)
 *   9. Methodology footer (financial-disclaimer tier)
 *
 * Includes Hi/En toggle and a collapsible Persona panel.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  AgeBand,
  CareerFamilyEntry,
  CareerFamilyWindowEntry,
  CareerForecastBlockEntry,
  CareerForecastPeriodEntry,
  CareerForecastTone,
  CareerSpecificFieldEntry,
  CareerV2Response,
  ChartCreateRequest,
  ConstraintFlag,
  PersonalisedInputs,
  Qualification,
  RiasecScores,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function CareerDashboard() {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [submittedReq, setSubmittedReq] = useState<ChartCreateRequest | null>(null);
  const [submittedAsOf, setSubmittedAsOf] = useState<string | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [personaOpen, setPersonaOpen] = useState(false);
  const [persona, setPersona] = useState<PersonalisedInputs | null>(null);

  // Evaluation date (anchors the 10-year career forecast). Defaults to
  // today; user can pick any future date to forecast from.
  const [asOf, setAsOf] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const reportMut = useMutation<
    CareerV2Response,
    Error,
    { req: ChartCreateRequest; asOf: string }
  >({
    mutationFn: ({ req, asOf }) => api.careerV2(req, asOf),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      const merged = persona ? { ...req, persona } : req;
      setSubmittedReq(merged);
      setSubmittedAsOf(asOf);
      reportMut.mutate({ req: merged, asOf });
    },
    [persona, reportMut, asOf],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 401) return t('फिर से प्रवेश करें', 'Please sign in again');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!submittedReq) return;
    try {
      setPdfDownloading(true);
      const blob = await api.careerV2PdfBlob(submittedReq, lang, submittedAsOf ?? undefined);
      const name = (submittedReq.name ?? 'career').replace(/\s+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `career_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [submittedReq, submittedAsOf, lang, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('करियर मार्गदर्शन', 'Career Guidance')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              '8 करियर-परिवार × 30 आधुनिक क्षेत्र; शास्त्रीय ज्योतिष + व्यक्तिगत सन्दर्भ।',
              '8 career families × 30 modern fields; classical jyotish + personal context.',
            )}
          </p>
        </div>
        <LangToggle lang={lang} setLang={setLang} />
      </header>

      <PersonaPanel
        open={personaOpen}
        setOpen={setPersonaOpen}
        persona={persona}
        setPersona={setPersona}
        t={t}
      />

      <ForecastDateInput asOf={asOf} setAsOf={setAsOf} t={t} />

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('विश्लेषण हो रहा है…', 'Analysing…')}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
          submittedName={submittedReq?.name ?? ''}
        />
      )}
    </div>
  );
}

// ─── Lang toggle (institute pattern) ────────────────────────────────────────

function LangToggle({
  lang, setLang,
}: {
  lang: 'hi' | 'en';
  setLang: (l: 'hi' | 'en') => void;
}) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
      {(['hi', 'en'] as const).map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => setLang(opt)}
          className={
            'px-3 py-1.5 text-xs font-medium transition '
            + (lang === opt
              ? 'bg-saffron-600 text-stone-950'
              : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
          }
          aria-pressed={lang === opt}
        >
          {opt === 'hi' ? 'हिन्दी' : 'English'}
        </button>
      ))}
    </div>
  );
}

// ─── Persona panel ──────────────────────────────────────────────────────────

const AGE_BANDS: AgeBand[] = ['under_18', '18_25', '25_35', '35_50', '50_plus'];
const QUALIFICATIONS: Qualification[] = [
  'below_class_10', 'class_10', 'class_12', 'diploma',
  'bachelors', 'masters', 'doctorate', 'professional',
];
const CONSTRAINTS: ConstraintFlag[] = [
  'financial_pressure', 'location_bound', 'family_business',
  'health_concern', 'single_earner', 'primary_caregiver',
];

// ─── Forecast date input — anchors the 10-year career forecast ─────────────

function ForecastDateInput({
  asOf,
  setAsOf,
  t,
}: {
  asOf: string;
  setAsOf: (d: string) => void;
  t: (hi: string, en: string) => string;
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const isToday = asOf === todayIso;
  return (
    <div className="rounded-2xl bg-stone-900/40 px-4 py-3 ring-1 ring-stone-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <label
            htmlFor="career-as-of"
            className="block text-xs font-semibold text-saffron-300"
          >
            {t('मूल्यांकन-तिथि (पूर्वानुमान का प्रारम्भ)', 'Evaluation date (forecast anchor)')}
          </label>
          <p className="mt-1 text-xs text-stone-500">
            {t(
              '10-वर्ष करियर पूर्वानुमान इसी तिथि से प्रारम्भ होता है। डिफ़ॉल्ट: आज।',
              '10-year career forecast is anchored from this date. Default: today.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="career-as-of"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="rounded-md bg-stone-950 px-3 py-1.5 text-sm text-stone-100 ring-1 ring-stone-700 focus:outline-none focus:ring-saffron-500"
          />
          {!isToday && (
            <button
              type="button"
              onClick={() => setAsOf(todayIso)}
              className="text-xs text-saffron-300 underline hover:text-saffron-200"
            >
              {t('आज', 'Today')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function PersonaPanel({
  open, setOpen, persona, setPersona, t,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  persona: PersonalisedInputs | null;
  setPersona: (p: PersonalisedInputs | null) => void;
  t: (hi: string, en: string) => string;
}) {
  const update = (patch: Partial<PersonalisedInputs>) => {
    const base: PersonalisedInputs = persona ?? {
      constraints: [],
      include_jain_remedies: false,
      include_secular_meditation: true,
    };
    setPersona({ ...base, ...patch });
  };
  const toggleConstraint = (c: ConstraintFlag) => {
    const current = persona?.constraints ?? [];
    const next = current.includes(c)
      ? current.filter((x) => x !== c)
      : [...current, c];
    update({ constraints: next });
  };
  const updateRiasec = (key: keyof RiasecScores, val: number) => {
    const base: RiasecScores = persona?.riasec ?? {
      realistic: 0, investigative: 0, artistic: 0,
      social: 0, enterprising: 0, conventional: 0,
    };
    update({ riasec: { ...base, [key]: val } });
  };

  return (
    <section className="rounded-2xl bg-stone-900/40 p-4 ring-1 ring-stone-800">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-sm font-semibold text-saffron-200">
            {t('व्यक्तिगत सन्दर्भ (वैकल्पिक)', 'Personalise (optional)')}
          </h2>
          <p className="mt-0.5 text-xs text-stone-500">
            {t(
              'आयु, योग्यता, बाधाएँ एवं रुचि-प्रोफाइल जोड़ें — सिफ़ारिशें अधिक सटीक होंगी।',
              'Add age, qualification, constraints, and interest profile — recommendations will sharpen.',
            )}
          </p>
        </div>
        <span className="text-saffron-300">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-4 border-t border-stone-800 pt-4 text-xs text-stone-200">
          <Field label={t('आयु-वर्ग', 'Age band')}>
            <select
              value={persona?.age_band ?? ''}
              onChange={(e) => update({ age_band: (e.target.value || null) as AgeBand | null })}
              className="rounded bg-stone-950 px-2 py-1 ring-1 ring-stone-700"
            >
              <option value="">—</option>
              {AGE_BANDS.map((a) => (
                <option key={a} value={a}>{a.replace('_', '-')}</option>
              ))}
            </select>
          </Field>
          <Field label={t('योग्यता', 'Qualification')}>
            <select
              value={persona?.qualification ?? ''}
              onChange={(e) => update({ qualification: (e.target.value || null) as Qualification | null })}
              className="rounded bg-stone-950 px-2 py-1 ring-1 ring-stone-700"
            >
              <option value="">—</option>
              {QUALIFICATIONS.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </Field>
          <Field label={t('वर्तमान कार्य', 'Current work')}>
            <input
              type="text"
              value={persona?.current_profession ?? ''}
              onChange={(e) => update({ current_profession: e.target.value || null })}
              placeholder={t('जैसे: सॉफ्टवेयर इंजीनियर', 'e.g., software engineer')}
              className="w-full max-w-xs rounded bg-stone-950 px-2 py-1 ring-1 ring-stone-700"
            />
          </Field>
          <Field label={t('बाधाएँ', 'Constraints')}>
            <div className="flex flex-wrap gap-2">
              {CONSTRAINTS.map((c) => {
                const active = (persona?.constraints ?? []).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleConstraint(c)}
                    className={
                      'rounded-full px-2 py-0.5 text-[11px] ring-1 transition '
                      + (active
                        ? 'bg-saffron-600 text-stone-950 ring-saffron-500'
                        : 'bg-stone-900 text-stone-300 ring-stone-700 hover:bg-stone-800')
                    }
                  >
                    {c.replace(/_/g, ' ')}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label={t('RIASEC रुचि-प्रोफाइल (0-5)', 'RIASEC interest profile (0-5)')}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {(
                [
                  ['realistic', t('R — व्यावहारिक', 'R — Realistic')],
                  ['investigative', t('I — अन्वेषक', 'I — Investigative')],
                  ['artistic', t('A — कलात्मक', 'A — Artistic')],
                  ['social', t('S — सामाजिक', 'S — Social')],
                  ['enterprising', t('E — उद्यमी', 'E — Enterprising')],
                  ['conventional', t('C — पारम्परिक', 'C — Conventional')],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between gap-2 text-[11px]">
                  <span>{label}</span>
                  <select
                    value={persona?.riasec?.[key as keyof RiasecScores] ?? 0}
                    onChange={(e) => updateRiasec(key as keyof RiasecScores, parseInt(e.target.value, 10))}
                    className="rounded bg-stone-950 px-1 py-0.5 ring-1 ring-stone-700"
                  >
                    {[0, 1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </Field>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-[11px] uppercase tracking-wider text-stone-500 sm:w-32 sm:shrink-0 sm:pt-1">
        {label}
      </span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

// ─── Report view ────────────────────────────────────────────────────────────

function ReportView({
  report, lang, t, onDownloadPdf, pdfDownloading, submittedName,
}: {
  report: CareerV2Response;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
  submittedName: string;
}) {
  return (
    <article className="space-y-4">
      {/* Header card with PDF download */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('करियर मार्गदर्शन रिपोर्ट', 'Career Guidance Report')}
          </p>
          <p className="mt-1 text-xl font-semibold text-saffron-100">{submittedName}</p>
          <p className="mt-0.5 text-xs text-stone-400">
            {report.direction.confidence === 'high'
              ? t('उच्च विश्वास', 'High confidence')
              : report.direction.confidence === 'medium'
                ? t('मध्यम विश्वास', 'Medium confidence')
                : t('न्यून विश्वास', 'Low confidence')}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading ? t('बन रहा है…', 'Generating…') : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      {/* Layer 1 — Takeaways */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('मुख्य निष्कर्ष', 'Key Takeaways')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t('सरल भाषा में', 'In plain language')}
        </p>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.takeaways_hi : report.takeaways_en).map((tk, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-saffron-400">●</span>
              <span>{tk}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Layer 2a — Direction + secondary + why_not */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('मुख्य करियर-दिशा', 'Primary Career Direction')}
        </h2>
        <div className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2.5">
          <p className="text-sm font-medium text-saffron-300">
            {lang === 'hi' ? report.direction.family_name_hi : report.direction.family_name_en}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-stone-100">
            {(lang === 'hi' ? report.direction.why_hi : report.direction.why_en).map((w, i) => (
              <li key={i}>· {w}</li>
            ))}
          </ul>
        </div>
        {report.secondary_direction && (
          <div className="mt-3">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
              {t('द्वितीय दिशा', 'Secondary Direction')}
            </p>
            <div className="rounded-md border-l-2 border-stone-600 bg-stone-900/40 px-3 py-2.5">
              <p className="text-sm font-medium text-stone-300">
                {lang === 'hi'
                  ? report.secondary_direction.family_name_hi
                  : report.secondary_direction.family_name_en}
              </p>
              <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-stone-200">
                {(lang === 'hi'
                  ? report.secondary_direction.why_hi
                  : report.secondary_direction.why_en
                ).map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </div>
          </div>
        )}
        {report.why_not && (
          <div className="mt-3 rounded-md bg-amber-950/20 px-3 py-2.5 ring-1 ring-amber-800/40">
            <p className="text-[10px] uppercase tracking-wider text-amber-300">
              {t('क्यों यह नहीं', 'Why not this')}
            </p>
            <p className="mt-1 text-sm font-medium text-amber-200">
              {lang === 'hi' ? report.why_not.family_name_hi : report.why_not.family_name_en}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-stone-200">
              {lang === 'hi' ? report.why_not.explanation_hi : report.why_not.explanation_en}
            </p>
          </div>
        )}
      </section>

      {/* Layer 2b — Recommended families */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('अनुशंसित करियर-परिवार', 'Recommended Career Families')}
        </h2>
        <div className="space-y-3">
          {report.recommended_families.map((f) => (
            <FamilyCard key={f.family_id} family={f} lang={lang} t={t} highlight />
          ))}
        </div>

        {report.other_families.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-stone-400 hover:text-saffron-300">
              {t('अन्य परिवार देखें', 'See other families')} ({report.other_families.length})
            </summary>
            <div className="mt-3 space-y-3">
              {report.other_families.map((f) => (
                <FamilyCard key={f.family_id} family={f} lang={lang} t={t} highlight={false} />
              ))}
            </div>
          </details>
        )}
      </section>

      {/* Layer 2c — Timing */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('दशा-समय एवं आउटलुक', 'Dasha & Timing Outlook')}
        </h2>
        <p className="text-xs text-stone-500">
          {t('वर्तमान दशा', 'Current dasha')}:{' '}
          <span className="text-stone-200">
            {lang === 'hi' ? report.timing.md_lord_hi : report.timing.md_lord_en} —{' '}
            {lang === 'hi' ? report.timing.ad_lord_hi : report.timing.ad_lord_en}
          </span>
        </p>
        <p className="mt-2 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.timing.md_outlook_hi : report.timing.md_outlook_en}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-stone-300">
          {lang === 'hi' ? report.timing.next_5_year_hi : report.timing.next_5_year_en}
        </p>
      </section>

      {/* Layer 2d — 10-year forecast */}
      {report.forecast && (
        <ForecastSection forecast={report.forecast} lang={lang} t={t} />
      )}

      {/* RIASEC alignment (when present) */}
      {report.riasec_alignment && (
        <section className="rounded-2xl bg-sky-950/20 p-5 ring-1 ring-sky-800/40">
          <h2 className="mb-2 text-base font-semibold text-sky-200">
            {t('रुचि-प्रोफाइल × चार्ट संगति', 'RIASEC × Chart Alignment')}
          </h2>
          <p className="text-sm leading-relaxed text-stone-100">
            {lang === 'hi' ? report.riasec_alignment.text_hi : report.riasec_alignment.text_en}
          </p>
        </section>
      )}

      {/* BNN section */}
      {report.bnn_section && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-1 text-base font-semibold text-saffron-200">
            {t('BNN — शनि-केन्द्रित कर्म-विश्लेषण', 'BNN — Saturn-Axis Karmic Reading')}
          </h2>
          <p className="mb-3 text-xs text-stone-500">
            {t(
              'भृगु नन्दि नाड़ी — शनि का भाव एवं पुरानी कर्म-दिशा।',
              'Bhrigu Nandi Nadi — Saturn-house and inherited karmic direction.',
            )}
          </p>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-stone-500">{t('शनि भाव', 'Saturn house')}:</span>{' '}
              <span className="text-stone-100">
                {report.bnn_section.saturn_house} —{' '}
                {lang === 'hi' ? report.bnn_section.saturn_rashi_hi : report.bnn_section.saturn_rashi_en}
              </span>
            </p>
            <p className="text-stone-100">
              {lang === 'hi' ? report.bnn_section.karmic_field_hi : report.bnn_section.karmic_field_en}
            </p>
            <p className="text-stone-100">
              <span className="text-stone-500">{t('गुरु-वृद्धि-दिशा', 'Jupiter growth')}:</span>{' '}
              {lang === 'hi' ? report.bnn_section.jupiter_growth_hi : report.bnn_section.jupiter_growth_en}
            </p>
            <p className="mt-2 border-t border-stone-800 pt-2 text-xs italic leading-relaxed text-stone-300">
              {lang === 'hi' ? report.bnn_section.bnn_summary_hi : report.bnn_section.bnn_summary_en}
            </p>
          </div>
        </section>
      )}

      {/* Inner Practice */}
      {report.inner_practice && (
        <InnerPracticeSection ip={report.inner_practice} lang={lang} t={t} />
      )}

      {/* Methodology footer */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">{lang === 'hi' ? m.method_hi : m.method_en}</span>
              <span className="text-stone-600"> — {lang === 'hi' ? m.source_hi : m.source_en}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

// ─── Forecast section (10-year AD-granular timeline) ──────────────────────

const TONE_TINT: Record<CareerForecastTone, { bg: string; ring: string; text: string; dot: string }> = {
  favourable: { bg: 'bg-emerald-950/30', ring: 'ring-emerald-800/50', text: 'text-emerald-200', dot: 'bg-emerald-500' },
  mixed:      { bg: 'bg-amber-950/30',   ring: 'ring-amber-800/50',   text: 'text-amber-200',   dot: 'bg-amber-500' },
  challenging:{ bg: 'bg-red-950/30',     ring: 'ring-red-800/50',     text: 'text-red-200',     dot: 'bg-red-500' },
};

function ForecastSection({
  forecast, lang, t,
}: {
  forecast: CareerForecastBlockEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-saffron-200">
          {t('10-वर्ष करियर पूर्वानुमान', '10-Year Career Forecast')}
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-stone-500">
          {t('आधार-तिथि', 'anchor')}: <span className="text-stone-300">{forecast.as_of}</span>
        </span>
      </header>
      <p className="mb-4 text-xs leading-relaxed text-stone-500">
        {t(
          'प्रत्येक अन्तर्दशा का स्वर — अनुकूल / मिश्रित / चुनौतीपूर्ण — ग्रह-स्वभाव, गृह-स्वामित्व एवं शनि-गुरु-राहु गोचर के संयोजन से।',
          'Each Antardaśā\'s tone — favourable / mixed / challenging — derived from the lord\'s nature, house-rulership, and Saturn-Jupiter-Rāhu transit overlay.',
        )}
      </p>

      {/* Yearly rollup — quick scan */}
      {forecast.yearly_rollup.length > 0 && (
        <div className="mb-5">
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            {t('वार्षिक सारांश', 'Year-by-year summary')}
          </h3>
          <ul className="space-y-1">
            {forecast.yearly_rollup.map((r) => {
              const tint = TONE_TINT[r.tone];
              return (
                <li
                  key={r.year}
                  className="flex items-baseline gap-2 text-xs text-stone-200"
                >
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${tint.dot}`} />
                  <span className="font-mono tabular-nums text-stone-400">{r.year}</span>
                  <span className="flex-1">
                    {lang === 'hi' ? r.one_liner_hi : r.one_liner_en}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Family-favourable windows */}
      {forecast.family_windows.length > 0 && (
        <FamilyWindowsBlock windows={forecast.family_windows} lang={lang} t={t} />
      )}

      {/* Detailed AD-granular timeline */}
      <div className="mt-5">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
          {t('विस्तृत अन्तर्दशा कालक्रम', 'Detailed Antardaśā timeline')}
        </h3>
        <div className="space-y-2">
          {forecast.periods.map((p) => (
            <ForecastPeriodRow key={`${p.label}-${p.start}`} period={p} lang={lang} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FamilyWindowsBlock({
  windows, lang, t,
}: {
  windows: CareerFamilyWindowEntry[];
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  // Group by family.
  const grouped = new Map<string, CareerFamilyWindowEntry[]>();
  for (const w of windows) {
    const list = grouped.get(w.family_id) ?? [];
    list.push(w);
    grouped.set(w.family_id, list);
  }
  return (
    <div className="mb-5 rounded-md bg-emerald-950/20 p-3 ring-1 ring-emerald-900/30">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
        {t(
          'अनुशंसित परिवार के लिए विशेष-अनुकूल खिड़कियाँ',
          'Specially-favourable windows for your top family',
        )}
      </h3>
      {Array.from(grouped.entries()).map(([fid, list]) => (
        <div key={fid} className="mb-2 last:mb-0">
          <p className="text-xs font-semibold text-emerald-100">
            {lang === 'hi' ? list[0].family_name_hi : list[0].family_name_en}
          </p>
          <ul className="mt-1 space-y-1">
            {list.map((w, i) => (
              <li key={i} className="text-xs leading-relaxed text-stone-300">
                <span className="font-mono tabular-nums text-stone-400">
                  {w.start} → {w.end}
                </span>
                {' · '}
                <span className="font-semibold text-emerald-200">
                  {lang === 'hi' ? w.ad_lord_hi : w.ad_lord_en}
                </span>
                <span className="ml-2 text-stone-400">
                  {lang === 'hi' ? w.why_hi : w.why_en}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ForecastPeriodRow({
  period, lang, t,
}: {
  period: CareerForecastPeriodEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const tint = TONE_TINT[period.tone];
  const transits = lang === 'hi' ? period.transit_headlines_hi : period.transit_headlines_en;
  return (
    <details
      className={`rounded-md ${tint.bg} px-3 py-2 ring-1 ${tint.ring}`}
    >
      <summary className="grid cursor-pointer grid-cols-[1.25rem_1fr_8rem_5rem] items-baseline gap-2 text-xs">
        <span className={`mt-1 h-2 w-2 rounded-full ${tint.dot}`} />
        <span className="font-medium text-stone-100">
          {lang === 'hi' ? period.md_lord_hi : period.md_lord_en}
          {' / '}
          <span className="text-saffron-200">
            {lang === 'hi' ? period.ad_lord_hi : period.ad_lord_en}
          </span>
        </span>
        <span className="font-mono tabular-nums text-stone-400">
          {period.start} → {period.end}
        </span>
        <span className={`text-right text-[10px] uppercase tracking-wider ${tint.text}`}>
          {lang === 'hi' ? period.tone_hi : period.tone_en}
        </span>
      </summary>
      <div className="mt-2 space-y-1.5 pl-7 text-xs leading-relaxed text-stone-300">
        {transits.length > 0 && (
          <div>
            <span className="text-[10px] uppercase tracking-wider text-stone-500">
              {t('गोचर', 'Transits')}:
            </span>
            <ul className="ml-2 list-disc pl-4">
              {transits.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}
        {(period.risk_hi || period.risk_en) && (
          <p>
            <span className="text-[10px] uppercase tracking-wider text-red-400">
              {t('जोखिम', 'Risk')}:
            </span>{' '}
            {lang === 'hi' ? period.risk_hi : period.risk_en}
          </p>
        )}
        {(period.remedy_hi || period.remedy_en) && (
          <p>
            <span className="text-[10px] uppercase tracking-wider text-emerald-400">
              {t('आचरण', 'Conduct')}:
            </span>{' '}
            {lang === 'hi' ? period.remedy_hi : period.remedy_en}
          </p>
        )}
      </div>
    </details>
  );
}


function FamilyCard({
  family, lang, t, highlight,
}: {
  family: CareerFamilyEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  highlight: boolean;
}) {
  const conf = family.confidence;
  const confLabel = conf === 'high' ? t('उच्च', 'High')
    : conf === 'medium' ? t('मध्यम', 'Med') : t('न्यून', 'Low');
  const confColour = conf === 'high'
    ? 'bg-emerald-900/40 text-emerald-200 ring-emerald-700/50'
    : conf === 'medium'
      ? 'bg-amber-900/40 text-amber-200 ring-amber-700/50'
      : 'bg-stone-800 text-stone-400 ring-stone-700';
  const borderColour = highlight
    ? 'border-saffron-700/60 bg-stone-900/40'
    : 'border-stone-700 bg-stone-900/30';

  return (
    <div className={'rounded-md border-l-2 px-3 py-3 ' + borderColour}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-saffron-300">
          {lang === 'hi' ? family.name_hi : family.name_en}
        </p>
        <span className={'rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ' + confColour}>
          {confLabel}
        </span>
      </div>
      <p className="mt-1 text-xs italic text-stone-400">
        {lang === 'hi' ? family.intro_hi : family.intro_en}
      </p>
      <ul className="mt-2 space-y-1.5">
        {family.top_specific_fields.map((sf) => (
          <SpecificFieldRow key={sf.field_id} sub={sf} lang={lang} t={t} />
        ))}
      </ul>
    </div>
  );
}

function SpecificFieldRow({
  sub, lang, t,
}: {
  sub: CareerSpecificFieldEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <li className="rounded bg-stone-950/40 px-2.5 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-stone-100">
          {lang === 'hi' ? sub.name_hi : sub.name_en}
        </p>
        <span className="text-[10px] tabular-nums text-stone-500">{sub.confidence}/13</span>
      </div>
      <p className="mt-0.5 text-[11px] leading-snug text-stone-300">
        {lang === 'hi' ? sub.why_hi : sub.why_en}
      </p>
      <p className="mt-0.5 text-[10px] italic leading-snug text-stone-500">
        {t('सावधानी', 'Trade-off')}: {lang === 'hi' ? sub.tradeoff_hi : sub.tradeoff_en}
      </p>
    </li>
  );
}

function InnerPracticeSection({
  ip, lang, t,
}: {
  ip: NonNullable<CareerV2Response['inner_practice']>;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-emerald-950/15 p-5 ring-1 ring-emerald-800/40">
      <h2 className="mb-1 text-base font-semibold text-emerald-200">
        {t('आन्तरिक अभ्यास', 'Inner Practice')}
      </h2>
      <p className="mb-3 text-xs text-stone-500">
        {t(
          'तीन भाग: इष्ट-देव जाप · द्वादश भावना · भावना-योग स्व-संकल्प।',
          'Three components: iṣṭa-deva recitation · Twelve Bhāvanā contemplations · Bhāvanā Yoga affirmations.',
        )}
      </p>

      {/* Iṣṭa-deva */}
      <div className="rounded-md border-l-2 border-emerald-600/60 bg-stone-900/40 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
          {t('दैनिक जाप', 'Daily Recitation')}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? ip.ishta_deva.text_hi : ip.ishta_deva.text_en}
        </p>
      </div>

      {/* Bhāvanā contemplations */}
      <div className="mt-3">
        <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
          {t('चिंतन-विषय (द्वादश भावना)', 'Contemplation themes (Twelve Bhāvanā)')}
        </p>
        <div className="space-y-2">
          {ip.bhavana_contemplations.map((b) => (
            <div key={b.bhavana_id} className="rounded bg-stone-950/40 px-2.5 py-1.5">
              <p className="text-sm font-medium text-emerald-200">
                {lang === 'hi' ? b.title_hi : b.title_en}
              </p>
              <p className="mt-0.5 text-xs leading-snug text-stone-200">
                {lang === 'hi' ? b.theme_hi : b.theme_en}
              </p>
              <p className="mt-1 text-xs italic text-emerald-300">
                {lang === 'hi' ? b.affirmation_hi : b.affirmation_en}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bhāvanā Yoga affirmations */}
      <div className="mt-3">
        <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
          {t('स्व-संकल्प (भावना-योग)', 'Self-Affirmations (Bhāvanā Yoga)')}
        </p>
        <ul className="space-y-1.5">
          {ip.affirmations.map((a, i) => (
            <li key={i} className="rounded bg-stone-950/40 px-2.5 py-1.5">
              <p className="text-sm leading-snug text-stone-100">
                {lang === 'hi' ? a.affirmation_hi : a.affirmation_en}
              </p>
              <p className="mt-0.5 text-[10px] italic text-stone-500">
                {t('चार्ट-संकेत', 'Chart trigger')}:{' '}
                {lang === 'hi' ? a.chart_signal_hi : a.chart_signal_en}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-3 border-t border-stone-800/60 pt-2 text-xs italic leading-relaxed text-stone-300">
        {lang === 'hi' ? ip.practice_instruction_hi : ip.practice_instruction_en}
      </p>
      <p className="mt-1 text-[10px] leading-snug text-stone-500">
        {lang === 'hi' ? ip.citation_hi : ip.citation_en}
      </p>
    </section>
  );
}
